import React, { useState } from 'react'
import { Modal, Form, Input, Button, Switch, Spin, Checkbox, Flex } from 'antd'
import { openNotificationWithIcon } from '../../../../../utils/notification.helper'
import { 
    useCreateOverruleReasoningMutation, 
    useUpdateOverruleReasoningMutation,
    useDeleteOverruleReasoningMutation
} from '../../../../../store/apis/overrule-reasoning.api'
import { CheckOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons'
import { Typography } from 'antd'
import AssignmentService from '../../../../../services/assignment.service'
import { useAutoFocusFirstInput } from '../../../../../../hooks/useAutoFocusFirstInput'
import { Perm, State } from '../../../../../constants/assignment-permissions'
import { useAssignmentAccess } from '../../access/Access'
import { ContentRules } from '../../access/access-rules/assignment-rules'

const { Text } = Typography

/**
 * Modal component for creating, updating, or revoking criterion reasoning overrules
 */
const OverruleReasoningModal = ({ 
    onCancel, 
    criterion, 
    token, 
    layerId, 
    patternId, 
    fileId, 
    existingOverrule,
}) => {
    const [form] = Form.useForm()
    useAutoFocusFirstInput()
    const [isReprojecting, setIsReprojecting] = useState(false)
    
    // API mutations
    const [createOverrule, { isLoading: isCreating }] = useCreateOverruleReasoningMutation()
    const [updateOverrule, { isLoading: isUpdating }] = useUpdateOverruleReasoningMutation()
    const [deleteOverrule, { isLoading: isDeleting }] = useDeleteOverruleReasoningMutation()
    
    // Track current criterionMet value to apply correct color to switch
    const isMet = Form.useWatch('criterionMet', form)

    // Combined loading state
    const isLoading = isCreating || isUpdating || isDeleting || isReprojecting
    
    // Handle form submission for create/update
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()
            
            let response
            
            if (existingOverrule) {
                response = await updateOverrule({
                    id: existingOverrule.id,
                    criterionMet: values.criterionMet,
                    reasoning: values.reasoning
                })
            } else {
                response = await createOverrule({
                    token,
                    layerId,
                    fileId,
                    motifId: patternId,
                    criterionIx: criterion.key,
                    criterionMet: values.criterionMet,
                    reasoning: values.reasoning
                })
            }
            
            if ('data' in response) {
                openNotificationWithIcon('success', 'Reasoning overruled successfully')
                await handleReproject(values.regroup)
                onCancel()
            }
        } catch (error) {
            console.error('Form validation failed:', error)
        }
    }

    // Handle deleting an overrule
    const handleRevoke = async () => {
        if (!existingOverrule?.id) return
        
        try {
            const response = await deleteOverrule({id: existingOverrule.id})
            
            if ('data' in response) {
                openNotificationWithIcon('success', 'Overrule reasoning has been revoked')
                await handleReproject(true)
                onCancel()
            }
        } catch (error) {
            console.error('Failed to revoke overrule:', error)
            openNotificationWithIcon('error', 'Failed to revoke overrule')
        }
    }
    
    const handleReproject = async regroup => {
        try {
            setIsReprojecting(true)
            await AssignmentService.updatePatternCriteriaAndProject(token, layerId, patternId, null, regroup, null)
            openNotificationWithIcon('success', 'Reprojection started')
        } catch (error) {
            console.error('Reprojection failed:', error)
            openNotificationWithIcon('error', 'Failed to reproject')
        } finally {
            setIsReprojecting(false)
        }
    }

    const { hasAccess: hasEditingPermission, errorMessage: missingPermissionErrorMessage } = useAssignmentAccess(ContentRules.EDIT_GRADINGS)
    
    return (
        <Modal
            title="Overrule Reasoning"
            open
            onCancel={onCancel}
            footer={null}
        >
            <Spin spinning={isLoading}>
                <Form form={form} layout="vertical" requiredMark={false}>
                    {/* Criterion and Met Switch Row */}
                    <Flex justify="space-between" align="top">
                        <Flex vertical>
                            <Text>Criterion</Text>
                            <Text>{criterion.criterion}</Text>
                        </Flex>
                        <Form.Item
                            name="criterionMet"
                            label="Met"
                            valuePropName="checked"
                            className='mb-16'
                            initialValue={existingOverrule ? existingOverrule.criterion_met : criterion.met}
                        >
                            <Switch 
                                checkedChildren={<CheckOutlined />} 
                                unCheckedChildren={<CloseOutlined />} 
                                style={{ backgroundColor: isMet ? 'green' : 'red' }} 
                                disabled={!hasEditingPermission}
                                title={hasEditingPermission ? 'Overrule reasoning' : missingPermissionErrorMessage}
                            />
                        </Form.Item>
                    </Flex>
                    <Flex vertical className='mb-16'>
                        <Text>Original Reasoning</Text>
                        <Text>{criterion.originalReasoning}</Text>
                    </Flex>
                    {/* Reasoning Textarea */}
                    <Form.Item
                        name="reasoning"
                        label="Reasoning"
                        rules={[{ required: true, message: 'Please enter reasoning' }]}
                        initialValue={existingOverrule?.reasoning}
                    >
                        <Input.TextArea rows={6} disabled={!hasEditingPermission} />
                    </Form.Item>
                    
                    {/* Footer with Regroup Option and Action Buttons */}
                    {hasEditingPermission && <Flex justify="space-between" align="middle">
                        <Form.Item name="regroup" className='mb-0' initialValue={true}>
                            <Checkbox>
                                Regroup
                            </Checkbox>
                        </Form.Item>
                        
                        <Flex gap={8}>
                            <Button onClick={onCancel}>
                                Cancel
                            </Button>
                            {existingOverrule && (
                                <Button 
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={isDeleting}
                                    onClick={handleRevoke}
                                >
                                    Revoke
                                </Button>
                            )}
                            <Button type="primary" onClick={handleSubmit} loading={isCreating || isUpdating}>
                                Overrule
                            </Button>
                        </Flex>
                    </Flex>}
                </Form>
            </Spin>
        </Modal>
    )
}

export default OverruleReasoningModal